// Session: who is signed in, the current business, and the logout wipe (§11c).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Me } from '@workdesk/shared/contracts';
import { ApiError, get, patch, post, setUnauthorizedHandler } from './api';
import { idbWipe } from './idb';
import { clearPendingMemory, loadPending, pendingSnapshot, replayPending, type AccessCheck } from './pending';
import { abortAll } from '../uploads/store';
import { fenceQueries, getCacheOwner, setCacheOwner } from './owner';
import { toast } from '../ui/Toast';
import { getTheme, persistTheme, setTheme, type ThemeMode } from './theme';
import {
  ACTING_MARKER, THEME_KEY_SAVED, broadcastIdentity, bumpIdentityGen, freezeImpersonation, impersonationOf, listenIdentity,
  onSessionChanged, setIdentity, setSessionChangedHandler,
} from './impersonation';

type SessionValue = {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
};

const Ctx = createContext<SessionValue>({ me: null, loading: true, refresh: async () => undefined });

export const ME_KEY = ['me'] as const;

/**
 * Fresh server access check for a business, used before any replay (§11c). Only a definite refusal
 * (403/404) counts as lost access; offline, 5xx, 429, 401 or an account change are 'unknown', so the
 * replay stops and keeps the item queued instead of stranding it as no-access.
 */
export async function checkBusinessAccess(businessId: string): Promise<AccessCheck> {
  try {
    await get(`/api/businesses/${businessId}/access`);
    return true;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return false;
    return 'unknown';
  }
}

/** Writes a /api/me response to the session cache only if it is still the signed-in account's. */
export function setMeIfCurrent(qc: QueryClient, m: Me | null | undefined) {
  if (m && m.id === getCacheOwner()) qc.setQueryData(ME_KEY, m);
}

/** sessionStorage key for a notice to show after the reload that follows an identity change. */
export const IDENTITY_NOTICE_KEY = 'wd.identityNotice';
const storage = (kind: 'local' | 'session'): Storage | null => {
  try {
    return kind === 'local' ? localStorage : sessionStorage;
  } catch {
    return null;
  }
};

let recoveryTried = false;
/**
 * Boot recovery (§6): a fresh load whose /me answers 401 while this browser still has an un-exited
 * impersonation (the tab was closed, the session expired) calls end once. `restored` → the staff session
 * is back, so /me is fetched again; otherwise the caller goes to /login.
 */
async function bootRecovery(): Promise<Me | null> {
  const marker = storage('local')?.getItem(ACTING_MARKER);
  if (recoveryTried || !marker) return null;
  recoveryTried = true;
  storage('local')?.removeItem(ACTING_MARKER);
  try {
    const r = await post<{ restored: boolean }>('/api/auth/impersonate/end', {});
    const saved = storage('session')?.getItem(THEME_KEY_SAVED) as ThemeMode | null;
    if (saved) setTheme(saved);
    storage('session')?.removeItem(THEME_KEY_SAVED);
    bumpIdentityGen();
    broadcastIdentity();
    if (!r?.restored) return null;
    return await get<Me>('/api/me');
  } catch {
    return null;
  }
}

/** Test hook: lets a test run boot recovery again. */
export const __resetBootRecovery = () => (recoveryTried = false);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await get<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          // Acting as a client and the session ended (expiry, revocation): freeze, and keep the cached Me
          // so nothing unmounts and every typed text stays (§6 Expiry UX).
          const cached = qc.getQueryData<Me | null>(ME_KEY);
          if (impersonationOf(cached)) {
            freezeImpersonation();
            return cached!;
          }
          return await bootRecovery();
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });
  const me = q.data ?? null;
  const nav = useNavigate();

  // Owner fence (§11c). Set during render so child queries hash under the new account in this same pass;
  // then drop the old owner's queries and abort the old owner's uploads.
  setCacheOwner(me?.id);
  setIdentity(me);
  const acting = !!impersonationOf(me);
  const prevOwner = useRef<string | null>(null);
  useEffect(() => {
    const next = me?.id ?? null;
    const prev = prevOwner.current;
    prevOwner.current = next;
    fenceQueries(qc);
    if (prev && prev !== next) void abortAll();
  }, [me?.id, qc]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Acting as a client: the session as the client ended. Freeze; never drop to login (§6).
      if (impersonationOf(qc.getQueryData<Me | null>(ME_KEY))) {
        freezeImpersonation();
        return;
      }
      // Session ended (expired or revoked): fence immediately, before anyone else can sign in here.
      setCacheOwner(null);
      fenceQueries(qc);
      void abortAll();
      qc.setQueryData(ME_KEY, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [qc]);

  // §2h: a confirmed identity change (another tab started or exited, or a stale tag) reloads this tab.
  useEffect(() => {
    setSessionChangedHandler(() => {
      storage('session')?.setItem(IDENTITY_NOTICE_KEY, 'Your account changed in another tab');
      qc.clear();
      window.location.reload();
    });
    const stop = listenIdentity(() => onSessionChanged());
    return () => {
      setSessionChangedHandler(null);
      stop();
    };
  }, [qc]);

  // Once per sign-in: adopt the user's synced theme. If the account still has the default ('system') but
  // this device has an explicit choice (e.g. picked on the login screen), save that choice to the account.
  // Never while acting as a client: their theme is theirs; the toggle stays local only (§6).
  useEffect(() => {
    if (!me || acting) return;
    const local = getTheme();
    if (me.theme === 'system' && local !== 'system') {
      void patch<Me>('/api/me', { theme: local })
        .then((m) => setMeIfCurrent(qc, m))
        .catch(() => undefined);
    } else if (me.theme !== local) setTheme(me.theme);
  }, [me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Replay offline mutations owned by this user (§11c). Not while acting: nothing is queued as a client,
  // and staff's own queued items replay after Exit.
  useEffect(() => {
    if (!me || acting) return;
    const uid = me.id;
    const run = async () => {
      await loadPending();
      if (getCacheOwner() !== uid) return;
      const r = await replayPending(uid, checkBusinessAccess, () => getCacheOwner() === uid);
      if (r.sent) {
        void qc.invalidateQueries();
        toast(`${r.sent} saved change${r.sent === 1 ? '' : 's'} sent`, 'success');
      }
      if (r.failed + r.held > 0) {
        const n = r.failed + r.held;
        toast(`${n} saved change${n === 1 ? " couldn't" : "s couldn't"} be sent`, 'error', {
          action: { label: 'Review', onClick: () => nav('/me#unsent') },
        });
      }
    };
    void run();
    window.addEventListener('online', run);
    return () => window.removeEventListener('online', run);
  }, [me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({ me, loading: q.isPending, refresh: q.refetch }), [me, q.isPending, q.refetch]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  return useContext(Ctx);
}

/** Signed-in user, for components rendered only inside authenticated routes. */
export function useMe(): Me {
  const { me } = useContext(Ctx);
  if (!me) throw new Error('useMe() outside an authenticated route');
  return me;
}

// ---------------- logout ----------------
type Hook = () => void | Promise<void>;
const logoutHooks = new Set<Hook>();
/** T8 registers push/SW clean-up here (unsubscribe, clear SW identity, close notifications). */
export function onLogout(fn: Hook): () => void {
  logoutHooks.add(fn);
  return () => logoutHooks.delete(fn);
}

/** Unsent items that would be discarded by logging out now (after trying to flush them). */
export async function unsentBeforeLogout(me: Me): Promise<number> {
  await loadPending();
  if (typeof navigator === 'undefined' || navigator.onLine) {
    // Same rules as any replay: fresh access check per business, and stop if the account changes.
    await replayPending(me.id, checkBusinessAccess, () => getCacheOwner() === me.id).catch(() => undefined);
  }
  return pendingSnapshot().filter((p) => p.ownerUserId === me.id).length;
}

/**
 * The §11c wipe. Call after the user confirmed any "N unsent changes will be discarded" warning.
 * Order: read synced theme → clear caches/stores/uploads/push → server logout (Clear-Site-Data) →
 * re-write the theme key → hard navigate to /login (no light flash for dark users).
 */
export async function logout(qc: QueryClient, me: Me | null): Promise<void> {
  const acting = !!impersonationOf(me);
  const theme: ThemeMode = acting ? ((storage('session')?.getItem(THEME_KEY_SAVED) as ThemeMode | null) ?? getTheme()) : (me?.theme ?? getTheme());
  if (acting) {
    storage('local')?.removeItem(ACTING_MARKER);
    storage('session')?.removeItem(THEME_KEY_SAVED);
  }
  await abortAll();
  for (const h of logoutHooks) {
    try {
      await h();
    } catch {
      /* best effort */
    }
  }
  setCacheOwner(null);
  qc.clear();
  clearPendingMemory();
  await idbWipe();
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'logout' });
  } catch {
    /* no SW */
  }
  try {
    await post('/api/auth/logout');
  } catch {
    /* offline: local wipe already done; server session expires or is revoked later */
  }
  persistTheme(theme);
  window.location.replace('/login');
}

export function useLogout() {
  const qc = useQueryClient();
  const { me } = useSession();
  return useCallback(() => logout(qc, me), [qc, me]);
}
